import { useEffect, useMemo, useState } from 'react'

const AUTOPLAY_DELAY = 5000

function htmlMarkup(html) {
  return { __html: html || '' }
}

function getBackgroundImage(image, overlay = 'default') {
  const safeImage = String(image || '').replace(/"/g, '%22')
  const gradient =
    overlay === 'carousel'
      ? 'linear-gradient(180deg, rgba(236, 218, 219, 0.12) 0%, rgba(255, 255, 255, 0.18) 100%)'
      : 'linear-gradient(90deg, rgba(244, 236, 238, 0.96) 0%, rgba(244, 236, 238, 0.78) 46%, rgba(244, 236, 238, 0.2) 100%)'

  return `${gradient}, url("${safeImage}")`
}

function CouponSlot({ html }) {
  if (!html) {
    return null
  }

  return <div dangerouslySetInnerHTML={htmlMarkup(html)} />
}

function BannerButton({ children, className, href, icon }) {
  if (!children) {
    return null
  }

  return (
    <a className={className} href={href || '#'}>
      <span>{children}</span>
      {icon && <i className={icon} aria-hidden="true" />}
    </a>
  )
}

function DefaultBanner({ payload }) {
  const content = payload.content || {}

  return (
    <section className={payload.className} style={payload.style}>
      <div
        className="nova-banner-widget__backdrop"
        style={{ backgroundImage: getBackgroundImage(payload.image, 'default') }}
      >
        <div className="nova-banner-widget__inner">
          <div className="nova-banner-widget__content">
            {content.eyebrow && <p className="nova-banner-widget__eyebrow">{content.eyebrow}</p>}
            {content.title && <h2 className="nova-banner-widget__title">{content.title}</h2>}
            {content.highlight && (
              <p className="nova-banner-widget__highlight">
                <i className="bi bi-star-fill" aria-hidden="true" />
                <span>{content.highlight}</span>
              </p>
            )}
            {content.description && <p className="nova-banner-widget__description">{content.description}</p>}

            <div className="nova-banner-widget__actions">
              <BannerButton
                className="nova-banner-widget__button"
                href={content.primary_button_url}
                icon="bi bi-arrow-right-short"
              >
                {content.primary_button_label}
              </BannerButton>
              <BannerButton className="nova-banner-widget__ghost-button" href={content.secondary_button_url}>
                {content.secondary_button_label}
              </BannerButton>
            </div>

            <CouponSlot html={payload.couponHtml} />
          </div>
        </div>
      </div>
    </section>
  )
}

function SplitBanner({ payload }) {
  const content = payload.content || {}

  return (
    <section className={payload.className} style={payload.style}>
      <div className="nova-banner-widget__split-media">
        <img
          className="nova-banner-widget__split-image"
          src={payload.image}
          alt={payload.labels?.imageAlt || 'Banniere principale du header'}
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
      </div>

      <div className="nova-banner-widget__split-panel">
        <div className="nova-banner-widget__content nova-banner-widget__content--split">
          {content.eyebrow && <p className="nova-banner-widget__eyebrow">{content.eyebrow}</p>}
          {content.title && <h2 className="nova-banner-widget__title nova-banner-widget__title--split">{content.title}</h2>}
          {content.highlight && (
            <p className="nova-banner-widget__highlight">
              <i className="bi bi-star-fill" aria-hidden="true" />
              <span>{content.highlight}</span>
            </p>
          )}
          {content.description && (
            <p className="nova-banner-widget__description nova-banner-widget__description--split">{content.description}</p>
          )}

          <div className="nova-banner-widget__actions">
            <BannerButton className="nova-banner-widget__soft-button" href={content.primary_button_url}>
              {content.primary_button_label}
            </BannerButton>
            <BannerButton className="nova-banner-widget__dark-button" href={content.secondary_button_url}>
              {content.secondary_button_label}
            </BannerButton>
          </div>

          <CouponSlot html={payload.couponHtml} />
        </div>
      </div>
    </section>
  )
}

function CarouselBanner({ payload }) {
  const content = payload.content || {}
  const images = useMemo(() => (Array.isArray(payload.images) && payload.images.length ? payload.images : [payload.image]), [payload])
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const hasControls = images.length > 1

  useEffect(() => {
    if (!hasControls || isPaused) {
      return undefined
    }

    const timer = window.setInterval(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % images.length)
    }, AUTOPLAY_DELAY)

    return () => window.clearInterval(timer)
  }, [hasControls, images.length, isPaused])

  const showSlide = (nextIndex) => {
    setActiveIndex((nextIndex + images.length) % images.length)
  }

  return (
    <section className={payload.className} style={payload.style} data-banner-react-carousel>
      <div
        className="nova-banner-widget__backdrop nova-banner-widget__backdrop--carousel"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onFocus={() => setIsPaused(true)}
        onBlur={() => setIsPaused(false)}
      >
        <div className="nova-banner-widget__carousel-slides">
          {images.map((image, index) => {
            const isActive = index === activeIndex

            return (
              <div
                className={`nova-banner-widget__carousel-slide${isActive ? ' is-active' : ''}`}
                aria-hidden={isActive ? 'false' : 'true'}
                key={`${image}-${index}`}
                style={{ backgroundImage: getBackgroundImage(image, 'carousel') }}
              />
            )
          })}
        </div>

        <div className="nova-banner-widget__inner">
          <div className="nova-banner-widget__content nova-banner-widget__content--center">
            {content.eyebrow && (
              <p className="nova-banner-widget__eyebrow nova-banner-widget__eyebrow--script">{content.eyebrow}</p>
            )}
            {content.title && <h2 className="nova-banner-widget__title nova-banner-widget__title--wide">{content.title}</h2>}
            {content.highlight && (
              <p className="nova-banner-widget__highlight">
                <i className="bi bi-star-fill" aria-hidden="true" />
                <span>{content.highlight}</span>
              </p>
            )}
            {content.description && <p className="nova-banner-widget__description">{content.description}</p>}

            <div className="nova-banner-widget__actions">
              <BannerButton className="nova-banner-widget__ghost-button" href={content.primary_button_url}>
                {content.primary_button_label}
              </BannerButton>
              <BannerButton className="nova-banner-widget__dark-button" href={content.secondary_button_url}>
                {content.secondary_button_label}
              </BannerButton>
            </div>

            <CouponSlot html={payload.couponHtml} />
          </div>

          {hasControls && (
            <>
              <button
                type="button"
                className="nova-banner-widget__carousel-arrow nova-banner-widget__carousel-arrow--prev"
                aria-label={payload.labels?.previousSlide || 'Afficher le slide precedent'}
                onClick={() => showSlide(activeIndex - 1)}
              >
                <i className="bi bi-chevron-left" aria-hidden="true" />
              </button>

              <button
                type="button"
                className="nova-banner-widget__carousel-arrow nova-banner-widget__carousel-arrow--next"
                aria-label={payload.labels?.nextSlide || 'Afficher le slide suivant'}
                onClick={() => showSlide(activeIndex + 1)}
              >
                <i className="bi bi-chevron-right" aria-hidden="true" />
              </button>

              <button
                type="button"
                className="nova-banner-widget__carousel-pause"
                aria-pressed={isPaused ? 'true' : 'false'}
                aria-label={
                  isPaused
                    ? payload.labels?.resumeCarousel || 'Relancer le carousel'
                    : payload.labels?.pauseCarousel || 'Mettre le carousel en pause'
                }
                onClick={() => setIsPaused((currentValue) => !currentValue)}
              >
                <i className={`bi ${isPaused ? 'bi-play-fill' : 'bi-pause-fill'}`} aria-hidden="true" />
              </button>

              <div className="nova-banner-widget__dots" role="tablist" aria-label={payload.labels?.carouselNavigation}>
                {images.map((image, index) => {
                  const isActive = index === activeIndex

                  return (
                    <button
                      type="button"
                      className={`nova-banner-widget__dot${isActive ? ' nova-banner-widget__dot--active' : ''}`}
                      aria-label={`${payload.labels?.goToSlide || 'Aller au slide'} ${index + 1}`}
                      aria-pressed={isActive ? 'true' : 'false'}
                      key={`${image}-dot-${index}`}
                      onClick={() => showSlide(index)}
                    />
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  )
}

export function MainBanner({ payload }) {
  if (!payload?.image) {
    return null
  }

  if (payload.type === 'demi-banner') {
    return <SplitBanner payload={payload} />
  }

  if (payload.type === 'carousel') {
    return <CarouselBanner payload={payload} />
  }

  return <DefaultBanner payload={payload} />
}
